import { useCallback, useMemo } from "react";

import { Car } from "../../services";
import { useTypedSelector } from "../../redux/store";
import { sortByNumberOrString } from "../../utils";
import { ColumnId } from "../../components/CarSelectTab/components/Table/columns";
import { ColumnId as ColumnIdAccountsTransport } from "../../constants/tables/accountsTransport";

const useCarSubHandler = (): useCarSubHandler.Props => {
	const lang = useTypedSelector((state) => state.session.language);

	const getDispatcherFullName = useCallback((item: Car.Model): string => {
		const person = item.responsibleDispatcher?.person;
		if (!person) return "";
		return [person.lastName, person.firstName, person.fatherName]
			.filter(Boolean)
			.join(" ")
			.trim();
	}, []);

	const getSortAndLimitCarSubHandler = useCallback(
		(
			{ limit, order }: useCarSubHandler.SmallSubscribeOptions,
			roleSubscribe: Car.Model[],
		): Car.Model[] => {
			if (!Array.isArray(roleSubscribe)) {
				return [];
			}

			let retval = [...(roleSubscribe || [])];

			const [isOrder, isLimit] = [
				typeof order === "object" &&
					order !== null &&
					!Array.isArray(order),
				typeof limit === "number",
			];

			if (isOrder) {
				const [[dataKey, sortType] = []] = Object.entries(order || {});
				const isSort =
					typeof sortType === "string" &&
					typeof dataKey === "string" &&
					["asc", "desc"].includes(sortType.toLowerCase());

				if (isSort) {
					const isAscending = sortType.toLowerCase() === "asc";
					const sortOrder = isAscending ? 1 : -1;

					if (dataKey === ColumnId.ParkNumber) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.parkNumber ?? -1;
							const nextValue = next.parkNumber ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Aliases) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								prev.executors
									?.map((executor) => executor?.alias || "")
									?.join(", ") ?? "";
							const nextValue =
								next.executors
									?.map((executor) => executor?.alias || "")
									?.join(", ") ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (
						dataKey === ColumnId.Mark ||
						dataKey === ColumnIdAccountsTransport.Brand
					) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								prev.model?.carBaseTypeToCarBrand?.carBrand
									?.name || "";
							const nextValue =
								next.model?.carBaseTypeToCarBrand?.carBrand
									?.name || "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Model) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.model?.name ?? "";
							const nextValue = next.model?.name ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.RegistrationNumber) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.registrationNumber ?? "";
							const nextValue = next.registrationNumber ?? "";
							return (
								sortByNumberOrString(
									prevValue,
									nextValue,
									true,
								) * sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Class) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.class?.name?.[lang] ?? "";
							const nextValue = next.class?.name?.[lang] ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.BodyType) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.bodyType?.name ?? "";
							const nextValue = next.bodyType?.name ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.RegisteredAt) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.createdAt
								? Date.parse(prev.createdAt) || 0
								: 0;
							const nextValue = next.createdAt
								? Date.parse(next.createdAt) || 0
								: 0;

							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.CarParkId) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.park?.name ?? "";
							const nextValue = next.park?.name ?? "";
							return (
								sortByNumberOrString(
									prevValue,
									nextValue,
									true,
								) * sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.ResponsibleDispatcher) {
						const sort = retval.sort((prev, next) => {
							const prevValue = getDispatcherFullName(prev) ?? -1;
							const nextValue = getDispatcherFullName(next) ?? -1;
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Company) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								prev.taxiService?.company?.name?.[lang] ?? "";
							const nextValue =
								next.taxiService?.company?.name?.[lang] ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.TaxiService) {
						const sort = retval.sort((prev, next) => {
							const prevValue =
								prev.taxiService?.settlement?.[lang] ?? "";
							const nextValue =
								next.taxiService?.settlement?.[lang] ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (dataKey === ColumnId.Color) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.color?.name?.[lang] ?? "";
							const nextValue = next.color?.name?.[lang] ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}

					if (
						dataKey === ColumnId.Note ||
						dataKey === ColumnIdAccountsTransport.Notes
					) {
						const sort = retval.sort((prev, next) => {
							const prevValue = prev.notes ?? "";
							const nextValue = next.notes ?? "";
							return (
								sortByNumberOrString(prevValue, nextValue) *
								sortOrder
							);
						});

						retval = sort;
					}
				}
			}

			if (isLimit) {
				retval = retval.slice(0, limit);
			}

			return retval;
		},
		[getDispatcherFullName, lang],
	);

	return useMemo(
		() => ({
			getSortAndLimitCarSubHandler,
		}),
		[getSortAndLimitCarSubHandler],
	);
};

declare namespace useCarSubHandler {
	interface Props {
		getSortAndLimitCarSubHandler: (
			data: SmallSubscribeOptions,
			roleSubscribe: Car.Model[],
		) => Car.Model[];
	}

	interface SmallSubscribeOptions {
		order?: Car.Order;
		limit?: number;
	}
}

export default useCarSubHandler;
